// ink-component
import React from 'react';
import * as uuid from 'uuid';

import TwiddleExpandAll from './components/TwiddleExpandAll/TwiddleExpandAll';
import TwiddleIcon from './components/TwiddleIcon/TwiddleIcon';
import { TwiddleRowContext } from './contexts';
import { useTwiddle, useTwiddleIconState } from './hooks';

/** The props passed to the NewTable.Twiddle render prop child */
interface TwiddleChildrenArguments {
  /** This Twiddle row's unique ID */
  id?: string | number;
  /** The current state of this Twiddle row */
  open: boolean;
  /** Invert the state of this Twiddle row */
  toggle: () => void;
  /** Explicitly open or close this Twiddle row */
  setOpen: (open: boolean) => void;
}

export interface NewTableTwiddleProps {
  /** Unique id to this Twiddle row */
  id?: string | number;
  /** Controlled Twiddle state */
  open?: boolean;
  /** Callback function for Twiddle contents */
  children: ((props: TwiddleChildrenArguments) => React.ReactNode) | React.ReactNode;
  /** @ignore */
  onToggle?: (open: boolean) => void;
  /** @ignore */
  level?: number;
}
const defaultOnToggle = () => undefined;

let warnedAboutIDGenerationPerformance = false;
const idWarning = '[NewTableTwiddle]: Twiddle rows without an ID will get a random ID, which could impact performance.';

/**
 * @group NewTable
 * @purpose Groups expandable rows
 * @categories table
 */
const NewTableTwiddle = ({
  id,
  children,
  level: levelProp = 0,
  open: openProp = false,
  onToggle = defaultOnToggle,
}: NewTableTwiddleProps) => {
  if (id === undefined && !warnedAboutIDGenerationPerformance) {
    console.warn(idWarning);
    warnedAboutIDGenerationPerformance = true;
  }

  const localId = React.useMemo(() => (id === undefined ? uuid.v4() : id), [id]);

  const { open, setOpen, toggle } = useTwiddle(localId, openProp);
  const { level: contextLevel = 0 } = useTwiddleIconState();

  const level = (levelProp || contextLevel) + 1;
  const rowContextValue = React.useMemo(() => ({ id: localId, level, open }), [localId, level, open]);

  const childrenProps: TwiddleChildrenArguments = React.useMemo(
    () => ({
      open,
      id: localId,
      setOpen: open => {
        setOpen(open);
        onToggle(open);
      },
      toggle: () => {
        toggle();
        onToggle(!open);
      },
    }),
    [localId, onToggle, open, setOpen, toggle],
  );

  return (
    <TwiddleRowContext.Provider value={rowContextValue}>
      {typeof children === 'function' ? children(childrenProps) : children}
    </TwiddleRowContext.Provider>
  );
};

NewTableTwiddle.defaultProps = {
  open: false,
};

NewTableTwiddle.Icon = TwiddleIcon;
NewTableTwiddle.ExpandAll = TwiddleExpandAll;

NewTableTwiddle.displayName = 'NewTable.Twiddle';

export default NewTableTwiddle;
